import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import FaqService from "../../../services/KangarooServices/FaqService";
import toast from "react-hot-toast";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import Select from "react-select";
import GroupForm from "./GroupForm";

const FaqCreate = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [initialData, setInitialData] = useState(null);
  const [faqGroupData, setFaqGroupData] = useState([]);
  const [isGroupOpen, setIsGroupOpen] = useState(false);

  const toggleGroup = () => {
    setIsGroupOpen(!isGroupOpen);
  };

  const fetchGroup = async () => {
    try {
      const response = await FaqService.getList();
      setFaqGroupData(response);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchDetail = async () => {
    if (!id) return;
    try {
      const response = await FaqService.getDetailFaq(id);
      setInitialData(response);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchGroup();
  }, []);

  useEffect(() => {
    fetchDetail();
  }, [id]);

  const faqGroupOption =
    faqGroupData?.map((type) => ({
      value: type.id,
      label: type.name,
    })) || [];

  const formik = useFormik({
    initialValues: {
      question: initialData?.question || "",
      answer: initialData?.answer || "",
      order: initialData?.order || "",
      faqGroupId: initialData?.faqGroupId || null,
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      question: Yup.string()
        .required("Question is required")
        .min(3, "Question must be at least 3 characters")
        .max(200, "Question must not exceed 200 characters"),
      answer: Yup.string().required("Answer is required")
        .min(3,"Answer must be at least 3 characters"),
    }),
    onSubmit: async (values) => {
      try {
        if (id) {
          await FaqService.update(id, values);
          toast.success("Update successful");
        } else {
          await FaqService.create(values);
          toast.success("Create successful");
        }
        navigate("/faq");
      } catch (error) {
        console.log(error);
        toast.error("Something went wrong");
      }
    },
  });

  return (
    <>
      <Container fluid>
        <div className="page-content">
          <BreadCrumb
            title="FAQ Form"
            pageTitle="FAQ"
            breadcrumbItems={[{ title: "Back to List", link: "/faq" }]}
          />

          <form onSubmit={formik.handleSubmit}>
            <div className="d-flex justify-content-end align-content-center align-items-center mb-3 gap-3">
              <Button
                type="submit"
                color="primary"
                // disabled={!formik.isValid}
              >
                {id ? "Update FAQ" : "Create FAQ"}
              </Button>
              <Button
                type="button"
                color="danger"
                onClick={() => navigate("/faq")}
              >
                Cancel
              </Button>
            </div>
            <Row>
              <Col lg={8}>
                <Card>
                  <CardBody>
                    {/* Question */}
                    <FormGroup className="mb-3">
                      <Label htmlFor="question">Question</Label>
                      <Input
                        id="question"
                        name="question"
                        value={formik.values.question}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched.question && !!formik.errors.question
                        }
                      />
                      {formik.touched.question && formik.errors.question && (
                        <FormFeedback>{formik.errors.question}</FormFeedback>
                      )}
                    </FormGroup>

                    {/* Answer (CKEditor) */}
                    <FormGroup className="mb-3">
                      <Label>
                        Answer <span className="text-danger">*</span>
                      </Label>
                      <CKEditor
                        editor={ClassicEditor}
                        data={formik.values.answer}
                        onChange={(event, editor) =>
                          formik.setFieldValue("answer", editor.getData())
                        }
                        onBlur={() => formik.setFieldTouched("answer", true)}
                      />
                      {formik.touched.answer && formik.errors.answer && (
                        <div
                          className="text-danger mt-1"
                          style={{ fontSize: "0.875em" }}
                        >
                          {formik.errors.answer}
                        </div>
                      )}
                    </FormGroup>
                  </CardBody>
                </Card>
              </Col>

              <Col lg={4}>
                <Card>
                  <CardBody>
                    {/* Order */}
                    <FormGroup className="mb-3">
                      <Label htmlFor="order">Order</Label>
                      <Input
                        id="order"
                        name="order"
                        type="number"
                        value={formik.values.order}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={formik.touched.order && !!formik.errors.order}
                      />
                      {formik.touched.order && formik.errors.order && (
                        <FormFeedback>{formik.errors.order}</FormFeedback>
                      )}
                    </FormGroup>

                    {/* FAQ Group */}
                    <div className="mb-3">
                      <Label>Faq Group</Label>
                      <Select
                        name="faqGroupId"
                        options={faqGroupOption}
                        value={faqGroupOption.find(
                          (opt) => opt.value === formik.values.faqGroupId
                        )}
                        onChange={(selected) =>
                          formik.setFieldValue(
                            "faqGroupId",
                            selected ? selected.value : null
                          )
                        }
                      />
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </form>
        </div>
      </Container>
    </>
  );
};

export default FaqCreate;
